import i18next from 'i18next';

import {notificationConstants, userActionTypes} from '../_constants';
import {userService} from '../_services';
import {alertActions, subscriptionActions} from './';
import {history} from '../_helpers';

export const userActions = {
    createAccount,
    login,
    logout,
    fetchUser,
    setPayment,
    clearRegistration,
    generateTokenForPasswordReset,
    resetPassword,
    changePassword,
    cancelAccount
};

/**
 * Redux Action method that registers the given username and password, with the paymentToken.
 * @param username
 * @param password
 * @param plan
 * @param paymentToken
 * @returns {Function}
 */
function createAccount(username, password, plan="", paymentToken = "") {
    return dispatch => {
        // utility constants - contains all module-related dispatches from this function
        const dispatchRequestStarted = (user) => dispatch({ type: userActionTypes.CREATE_USER_REQUEST, user });
        const dispatchRequestSuccess = (user) => dispatch({ type: userActionTypes.CREATE_USER_SUCCESS, user });
        const dispatchRequestError  = (error) => dispatch({ type: userActionTypes.CREATE_USER_FAILURE, error });

        // dispatch that a user is being created
        dispatchRequestStarted(username);

        // pass the parameters to the service
        userService.createAccount(username, password, plan, paymentToken)
            .then(
                user => {
                    if( user && user.email ) {
                        // dispatch a success if the service returns a user and email
                        dispatchRequestSuccess(user);
                        dispatch(alertActions.success("User successfully created."));
                    } else {
                        dispatchRequestSuccess(null);
                        dispatch(alertActions.success("The account was successfully created, but was difficult to fetch it at the moment. Try logging in."));
                    }
                    // clear the registration data
                    dispatch(clearRegistration());
                    // login as the new user
                    dispatch(login(username, password, true));
                },
                error => {
                    // dispatch a failure for the user creation and alert engine with appropriate message
                    dispatchRequestError(error);
                    dispatch( alertActions.error(error) );
                }
            );
    };
}

/**
 * Redux Action method that authenticates the username and password and gets the access_token if successful
 * @param username email of the account
 * @param password password for the account
 * @param isFirstTimeUser indicates if user is logging for the first time, default false
 * @returns {function(...[*]=)}
 */
function login(username, password, isFirstTimeUser = false) {
    return dispatch => {
        // utility constants
        const dispatchRequestStarted = (user) => dispatch({ type: userActionTypes.LOGIN_REQUEST, user });
        const dispatchRequestSuccess = (user) => dispatch({ type: userActionTypes.LOGIN_SUCCESS, user });
        const dispatchRequestError  = () => dispatch({ type: userActionTypes.LOGIN_FAILURE });

        // dispatch that user is being attempted to be authorized
        dispatchRequestStarted(username);

        // pass the API request to the service and parse the result
        userService.login(username, password)
            .then(
                user => {
                    if( user && user.username ){
                        // dispatch the user has been authenticated with details
                        dispatchRequestSuccess(user);
                        dispatch(alertActions.success("Successfully logged in."));

                        dispatch(subscriptionActions.getSubscription());    // verify subscription once user logs in
                        dispatch(userActions.fetchUser());                  // get user information including integrations

                        (isFirstTimeUser)
                            ? history.push("/welcome")  // welcome the first-time user
                            : history.push("/media");   // regular media landing page

                    } else {
                        // if access token is not present, the user will not be able to get successful responses
                        dispatchRequestError();
                        dispatch(alertActions.error("The user couldn't be properly authenticated at the moment. Try logging in again."));
                    }
                },
                error => {
                    // dispatch a failure on user authentication and alert engine with appropriate message
                    dispatchRequestError();
                    dispatch(alertActions.error(error));
                }
            );
    };
}

/**
 * This function logs out the current user
 * @returns {{type: *}}
 */
function logout() {
    const t = i18next.t;

    return dispatch => {
        const dispatchRequestSuccess = () => dispatch({ type: userActionTypes.LOGOUT });

        userService.logout()    // removes the authentication storage that has user details
            .then(() => {
                dispatchRequestSuccess();
                dispatch(alertActions.success("Successfully logged out."));

                history.push("/signin");  // push to signin after log out
            })
            .catch(() => {
                // notify the user about logout failure
                let res = {
                    "status": 500,
                    "errorMsg": t("Sorry, something went wrong. Please try logging out again."),
                    "toastParams": {
                        "toastType": notificationConstants.NOTIFICATION_TYPES.ERROR
                    },
                };
                dispatch(alertActions.error(res));
            });
    }
}

/**
 * This function gets the current user in the session based on access_tokens
 * @returns {Function}
 */
function fetchUser() {
    return dispatch => {
        // utility constants
        const dispatchRequestStarted = () => dispatch({ type: userActionTypes.GET_USER_REQUEST });
        const dispatchRequestSuccess = (user) => dispatch({ type: userActionTypes.GET_USER_SUCCESS, user });
        const dispatchRequestError  = (error) => dispatch({ type: userActionTypes.GET_USER_FAILURE, error });

        // dispatch the user is being fetched
        dispatchRequestStarted();

        // delegate the API request to the service
        userService.fetchUser()
            .then(
                user => {
                    // dispatch success
                    dispatchRequestSuccess(user);
                },
                error => {
                    // dispatch on error on fetching the current user
                    dispatchRequestError(error);
                    dispatch(alertActions.error(error));
                }
            );
    };
}

/**
 * This function updates the user's payment information
 *
 * @param stripeToken   Token
 * @returns {Function}
 */
function setPayment(stripeToken) {
    const t = i18next.t;

    return dispatch => {
        // utility constants
        const dispatchRequestStarted = () => dispatch({ type: userActionTypes.SET_PAYMENT_REQUEST});
        const dispatchRequestSuccess = () => dispatch({ type: userActionTypes.SET_PAYMENT_SUCCESS});
        const dispatchRequestError  = (error) => dispatch({ type: userActionTypes.SET_PAYMENT_FAILURE, error });

        dispatchRequestStarted();

        userService.setPayment(stripeToken)
            .then(
                () => {
                    dispatchRequestSuccess();
                    dispatch(alertActions.success({successMsg: t("Payment info successfully updated.")}));

                    // Push the user to "media" page
                    history.push("/media");
                },
                error => {
                    dispatchRequestError(error);
                    dispatch(alertActions.error( {errorMsg: error.errorMsg} ));
                }
            );
    };
}

/**
 * This function clears the current set of registration data
 * @returns {Function}
 */
function clearRegistration() {
    return dispatch => {
        const dispatchRequestSuccess = () => dispatch({ type: userActionTypes.REGISTRATION_VALIDATE_USER_CLEAR });

        dispatchRequestSuccess();
        dispatch(alertActions.success("Successfully cleared registration data."));
    }
}

/**
 * This function initiates token generation for password reset
 * @param username the email of the account for which password needs to be reset
 * @returns {Function}
 */
function generateTokenForPasswordReset(username) {
    const t = i18next.t;

    return dispatch => {
        // utility constants
        const dispatchRequestStarted = () => dispatch({ type: userActionTypes.PASSWORD_TOKEN_REQUEST });
        const dispatchRequestSuccess = () => dispatch({ type: userActionTypes.PASSWORD_TOKEN_SUCCESS });
        const dispatchRequestError  = (error) => dispatch({ type: userActionTypes.PASSWORD_TOKEN_FAILURE, error });

        dispatchRequestStarted();

        userService.generatePasswordResetToken(username)
            .then(
                () => {
                    dispatchRequestSuccess();
                    dispatch(alertActions.success( t("To make sure we’ve got the right account, we’ve sent you an email. Please follow the instructions in that email to finish resetting your password.")
                    ));
                },
                error => {
                    dispatchRequestError(error);
                    dispatch(alertActions.error( error ));
                }
            );
    };
}

/**
 * This function accepts credentials and invokes password change
 * @param username the email of the account
 * @param token the unique 'validity-limited' token for password reset
 * @param password the new password
 * @returns {Function}
 */
function resetPassword(username, token, password){
    const t = i18next.t;

    return dispatch => {
        // utility constants
        const dispatchRequestStarted = () => dispatch({ type: userActionTypes.PASSWORD_RESET_REQUEST });
        const dispatchRequestSuccess = () => dispatch({ type: userActionTypes.PASSWORD_RESET_SUCCESS });
        const dispatchRequestError  = (error) => dispatch({ type: userActionTypes.PASSWORD_RESET_FAILURE, error });

        dispatchRequestStarted();

        userService.setNewPassword(username, token, password)
            .then(
                () => {
                    dispatchRequestSuccess();
                    dispatch(alertActions.success( t("Password was successfully reset. Signing in user with new password.")
                    ));

                    // login with the new password
                    dispatch(login(username, password));
                },
                error => {
                    dispatchRequestError(error);
                    dispatch(alertActions.error( error ));
                }
            );
    };
}

/**
 * This function changes the password of the current user to the given new password
 * @param oldPassword the old password
 * @param newPassword the new password
 * @returns {Function}
 */
function changePassword(oldPassword, newPassword) {
    const t = i18next.t;

    return dispatch => {
        // utility constants
        const dispatchRequestStarted = () => dispatch({type: userActionTypes.PASSWORD_CHANGE_REQUEST});
        const dispatchRequestSuccess = () => dispatch({type: userActionTypes.PASSWORD_CHANGE_SUCCESS});
        const dispatchRequestError = (error) => dispatch({type: userActionTypes.PASSWORD_CHANGE_FAILURE, error});

        dispatchRequestStarted();

        userService.changePassword(oldPassword, newPassword)
            .then(
                () => {
                    dispatchRequestSuccess();
                    dispatch(alertActions.success(t("Password was successfully changed. Signing in user with new password.")
                    ));

                    let user = userService.getAuthUser();
                    if(user && user.username) {
                        // login with the new password
                        dispatch(login(user.username, newPassword));
                    } else {
                        // (fallback) if user is not available redirect to login page
                        history.push("/signin");
                    }
                },
                error => {
                    dispatchRequestError(error);
                    dispatch(alertActions.error({errorMsg: error.errorMsg}));
                }
            );
    };
}

/**
 * This function cancels the current user's account
 *
 * @returns {Function}
 */
function cancelAccount() {
    return dispatch => {
        // utility constants
        const dispatchRequestStarted = () => dispatch({type: userActionTypes.CANCEL_ACCOUNT_REQUEST});
        const dispatchRequestSuccess = () => dispatch({type: userActionTypes.CANCEL_ACCOUNT_SUCCESS});
        const dispatchRequestError = (error) => dispatch({type: userActionTypes.CANCEL_ACCOUNT_FAILURE, error});

        dispatchRequestStarted();

        userService.cancelAccount()
            .then(
                () => {
                    dispatchRequestSuccess();
                },
                error => {
                    dispatchRequestError(error);
                    dispatch(alertActions.error({errorMsg: error.errorMsg}));
                }
            );
    };
}
